/**
 * Plan — MOBILE.
 *
 * El colchón como número grande y, debajo, la lista de los próximos días. Lista y no grilla de
 * mes: en 360 px una celda de calendario tiene 45 px de ancho y no cabe el día de rutina que
 * toca, que es justamente el dato que se viene a buscar.
 */

import { useState, type ReactNode } from 'react';
import { nombreDia } from '@mg/core';

import { diaMes, queFaltaBloqueo, useCalendario } from '../features/useCalendario.ts';
import { useRuta } from '../ui/router.tsx';
import { HojaInferior } from '../ui/Shell.mobile.tsx';
import { Aviso, Campo, Icono, Insignia, PilaDePlan, Vacio } from '../ui/componentes.tsx';

export function CalendarioMobile(): ReactNode {
  const c = useCalendario();
  const { ir } = useRuta();
  const [bloqueando, setBloqueando] = useState(false);

  if (c.datos.cargando) return <Vacio>Cargando…</Vacio>;
  if (!c.calendario || !c.plan) {
    return (
      <section className="tarjeta">
        <h2 className="titulo">Todavía no hay un paquete cargado</h2>
        <p className="nota" style={{ marginTop: 'var(--e2)' }}>
          El plan es el paquete de sesiones que compraste: cuántas son y cuándo vencen. Con eso esta
          pantalla te dice si te alcanza el tiempo.
        </p>
        <button
          type="button"
          className="boton boton--principal"
          style={{ marginTop: 'var(--e4)' }}
          onClick={() => ir('ajustes', 'rutina')}
        >
          Cargar un paquete
        </button>
      </section>
    );
  }

  const proximos = c.dias.filter((d) => d.fecha >= c.datos.hoy);

  return (
    <>
      {/* El modelo entero en una línea: sin esto, "COLCHÓN 4" no dice qué vence ni qué son las 12.
          Va en cuerpo de texto y no en `.nota`: es la frase que explica la pantalla entera, y era
          el texto más chico de toda la vista. */}
      <p style={{ lineHeight: 1.45 }}>
        Paquete de <span className="lectura">{c.plan.sesionesTotales}</span> sesiones · vence el{' '}
        <span className="lectura">
          {c.datos.planActivo ? diaMes(c.datos.planActivo.fecha_vencimiento) : '—'}
        </span>{' '}
        · te quedan <span className="lectura">{c.plan.restantes}</span>.
      </p>

      <section className="tarjeta">
        <span className="eyebrow">Colchón</span>
        <div className="fila" style={{ alignItems: 'baseline', gap: 8 }}>
          <span
            className="cifra-heroe"
            style={{ color: c.calendario.colchonCritico ? 'var(--ambar)' : undefined }}
          >
            {c.calendario.colchon}
          </span>
          <span className="unidad" style={{ fontSize: 18 }}>
            {Math.abs(c.calendario.colchon) === 1 ? 'día' : 'días'}
          </span>
        </div>
        <p className="nota" style={{ marginTop: 'var(--e2)' }}>
          <span className="lectura">{c.calendario.diasHabiles}</span> días hábiles −{' '}
          <span className="lectura">{c.calendario.sesionesPendientes}</span> pendientes
        </p>

        <div style={{ marginTop: 'var(--e4)' }}>
          <PilaDePlan
            usadas={c.plan.usadas}
            pendientes={c.plan.restantes}
            enRiesgo={c.calendario.sesionesEnRiesgo}
            alto={24}
          />
          <div className="fila fila--entre" style={{ marginTop: 6 }}>
            <span className="nota--fina">
              <span className="lectura">{c.plan.usadas}</span> usadas ·{' '}
              <span className="lectura">{c.plan.restantes}</span> quedan
            </span>
            <span className="nota--fina">
              <span className="lectura">{c.plan.diasHastaVencimiento}</span> días para el vencimiento
            </span>
          </div>
          {/* Las placas rojas de la pila no se explican solas. */}
          {c.calendario.sesionesEnRiesgo > 0 && (
            <p className="nota" style={{ color: 'var(--ladrillo)', marginTop: 6 }}>
              Van a sobrar <span className="lectura">{c.calendario.sesionesEnRiesgo}</span> al
              vencimiento: no entran antes de esa fecha.
            </p>
          )}
        </div>
      </section>

      {c.avisos.map((a) => (
        <Aviso key={a.clave} tono={a.tono}>
          {a.texto}
        </Aviso>
      ))}

      {c.ultimosCambios.length > 0 && (
        <Aviso tono="info" titulo="Qué cambió">
          {c.ultimosCambios.map((x) => (
            <span key={x.texto} style={{ display: 'block' }}>
              {x.texto}
            </span>
          ))}
          <button
            type="button"
            className="boton boton--chico boton--fantasma"
            style={{ marginTop: 8 }}
            onClick={c.limpiarCambios}
          >
            Entendido
          </button>
        </Aviso>
      )}

      {c.simulando && (
        <Aviso tono="atencion" titulo={`Si no vas el ${nombreDia(c.simulando.fecha)}`}>
          {c.simulando.cambios.length === 0
            ? 'No cambia nada: te sigue alcanzando.'
            : c.simulando.cambios.map((x) => (
                <span key={x.texto} style={{ display: 'block' }}>
                  {x.texto}
                </span>
              ))}
          <div className="fila" style={{ gap: 8, marginTop: 10 }}>
            <button
              type="button"
              className="boton boton--chico"
              onClick={() => void c.bloquear(c.simulando!.fecha, c.simulando!.fecha, 'No pude ir')}
            >
              Bloquear ese día
            </button>
            <button type="button" className="boton boton--chico boton--fantasma" onClick={c.limpiarSimulacion}>
              Cancelar
            </button>
          </div>
        </Aviso>
      )}

      <button type="button" className="boton" onClick={() => setBloqueando(true)}>
        <Icono nombre="calendario" tamano={18} />
        Bloquear días (viaje, cierre)
      </button>

      {/* La última tarjeta quedaba debajo de la barra de tabs: la lista necesita su propio aire. */}
      <section className="columna" style={{ gap: 'var(--e2)', paddingBottom: 'var(--e6)' }}>
        <span className="eyebrow">Próximos días</span>

        {proximos.length === 0 && (
          <>
            <Vacio>El plan ya venció.</Vacio>
            <button type="button" className="boton boton--principal" onClick={() => ir('ajustes', 'rutina')}>
              Cargar un paquete
            </button>
          </>
        )}

        {proximos.map((d) => {
          const noDisponible = d.cerrado || d.bloqueado !== null;
          return (
            <div
              key={d.fecha}
              className="tarjeta"
              style={{
                padding: 'var(--e3)',
                opacity: noDisponible ? 0.5 : 1,
                borderColor: d.esHoy ? 'var(--ambar)' : undefined,
              }}
            >
              <div className="fila fila--entre">
                <div>
                  <div className="fila" style={{ gap: 6 }}>
                    <span className="lectura" style={{ fontSize: 15 }}>
                      {d.fecha.slice(8)}/{d.fecha.slice(5, 7)}
                    </span>
                    <span className="nota--fina" style={{ textTransform: 'capitalize' }}>
                      {nombreDia(d.fecha)}
                    </span>
                    {d.esHoy && <Insignia tono="ambar">hoy</Insignia>}
                  </div>

                  <p style={{ fontSize: 14, marginTop: 2 }}>
                    {d.entrada ? (
                      <>
                        {d.entrada.nombre}
                        {d.entrada.fijada && (
                          <>
                            {' '}
                            <Insignia>agendada</Insignia>
                          </>
                        )}
                      </>
                    ) : d.cerrado ? (
                      <span className="apagado">Gimnasio cerrado</span>
                    ) : d.bloqueado ? (
                      <span className="apagado">{d.bloqueado.motivo}</span>
                    ) : d.descanso ? (
                      <span className="apagado">Descanso por recuperación</span>
                    ) : d.sesionReal?.estado === 'completada' ? (
                      <span className="apagado">Sesión hecha</span>
                    ) : (
                      <span className="apagado">Libre</span>
                    )}
                  </p>

                  {d.entrada?.conflicto && (
                    <p className="nota--fina" style={{ color: 'var(--ambar)', marginTop: 2 }}>
                      {d.entrada.conflicto.recomendacion}
                    </p>
                  )}
                </div>

                {d.bloqueado ? (
                  <button
                    type="button"
                    className="boton boton--chico boton--fantasma"
                    onClick={() => void c.liberar(d.bloqueado!.id, d.fecha)}
                  >
                    Liberar
                  </button>
                ) : d.entrada && !d.cerrado ? (
                  <button type="button" className="boton boton--chico boton--fantasma" onClick={() => c.simular(d.fecha)}>
                    ¿Y si falto?
                  </button>
                ) : null}
              </div>
            </div>
          );
        })}
      </section>

      {bloqueando && (
        <HojaInferior titulo="Bloquear días" onCerrar={() => setBloqueando(false)}>
          <FormularioBloqueo c={c} onListo={() => setBloqueando(false)} />
        </HojaInferior>
      )}
    </>
  );
}

function FormularioBloqueo({
  c,
  onListo,
}: {
  c: ReturnType<typeof useCalendario>;
  onListo: () => void;
}): ReactNode {
  const [desde, setDesde] = useState(c.manana);
  const [hasta, setHasta] = useState(c.manana);
  const [motivo, setMotivo] = useState('Viaje');

  // flujo.md §3: un botón deshabilitado siempre dice qué falta. Las dos fechas se pueden vaciar
  // desde el propio campo, y ahí el botón quedaba gris sin explicación.
  const falta = queFaltaBloqueo(desde, hasta);

  return (
    <>
      <p className="nota">
        Los días bloqueados salen del cálculo del colchón. Si el día ya era de cierre del gimnasio,
        no se cuenta dos veces.
      </p>
      <Campo etiqueta="Desde" texto type="date" value={desde} onChange={(e) => setDesde(e.target.value)} />
      <Campo
        etiqueta="Hasta"
        texto
        type="date"
        value={hasta}
        min={desde}
        onChange={(e) => setHasta(e.target.value)}
      />
      <Campo etiqueta="Motivo" texto value={motivo} onChange={(e) => setMotivo(e.target.value)} />
      <button
        type="button"
        className="boton boton--principal boton--registrar"
        disabled={falta !== null}
        onClick={async () => {
          await c.bloquear(desde, hasta, motivo);
          onListo();
        }}
      >
        Bloquear y recalcular
      </button>
      {falta && <p className="nota--fina">{falta}</p>}
    </>
  );
}
